import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { site } from "@/data/site";

const links = [
    { to: "/", label: "Forside" },
    { to: "/ydelser", label: "Ydelser" },
    { to: "/projekter", label: "Projekter" },
    { to: "/om", label: "Om os" },
    { to: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-navbar"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-white/90 backdrop-blur-xl border-b border-[var(--ns-border)] shadow-sm"
                    : "bg-white/70 backdrop-blur-md border-b border-transparent"
            }`}
        >
            <div className="container-ns flex items-center justify-between h-16 sm:h-20">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src={site.logo}
                        alt="NS Tømrer og Ejendomsservice logo"
                        className="h-11 sm:h-12 w-auto object-contain"
                    />
                    <div className="leading-tight hidden sm:block">
                        <div className="font-display font-extrabold text-[15px] text-[var(--ns-text)] tracking-tight">
                            NS Tømrer
                        </div>
                        <div className="text-[11px] text-[var(--ns-muted)] tracking-wider uppercase">
                            & Ejendomsservice
                        </div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                            className={({ isActive }) =>
                                `link-underline font-display text-sm font-bold tracking-wide ${
                                    isActive
                                        ? "text-[var(--ns-blue)] active"
                                        : "text-[var(--ns-text)] hover:text-[var(--ns-blue)]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <a
                        href={site.phoneHref}
                        data-testid="nav-call-button"
                        className="btn-primary !py-2.5 !px-5 text-sm"
                    >
                        <Phone className="w-4 h-4" />
                        {site.phone}
                    </a>
                </div>

                <button
                    type="button"
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden p-2 rounded-md text-[var(--ns-text)] hover:bg-[var(--ns-blue-soft)] transition"
                    aria-label="Åbn menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-[var(--ns-border)] bg-white">
                    <div className="container-ns py-4 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                data-testid={`nav-mobile-link-${l.to.replace("/", "") || "home"}`}
                                className={({ isActive }) =>
                                    `py-3 px-3 rounded-md font-display font-bold text-base ${
                                        isActive
                                            ? "bg-[var(--ns-blue-soft)] text-[var(--ns-blue)]"
                                            : "text-[var(--ns-text)] hover:bg-[var(--ns-blue-soft)]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <a
                            href={site.phoneHref}
                            data-testid="nav-mobile-call-button"
                            className="btn-primary mt-3 w-full"
                        >
                            <Phone className="w-4 h-4" />
                            Ring {site.phone}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
